import { useMemo } from 'react';
import { useNostr } from '@nostrify/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

import { HIGHLIGHT_KIND, parseHighlight, type Highlight } from '@/lib/highlights';
import { useContacts } from '@/hooks/useContacts';
import { useCurrentUser } from '@/hooks/useCurrentUser';

const PAGE_SIZE = 25;

export type HighlightsFeedMode = 'global' | 'following' | 'mine';

/** Parse + dedupe raw pages of highlight events. */
export function useParsedPages(pages: NostrEvent[][] | undefined): Highlight[] {
  return useMemo(() => {
    const seen = new Set<string>();
    const highlights: Highlight[] = [];

    for (const page of pages ?? []) {
      for (const event of page) {
        if (seen.has(event.id)) continue;
        seen.add(event.id);
        const highlight = parseHighlight(event);
        if (highlight) highlights.push(highlight);
      }
    }

    return highlights;
  }, [pages]);
}

/**
 * Infinite-scrolling feed of NIP-84 highlights.
 *
 * - `global`: every highlight the relays return.
 * - `following`: highlights from the logged-in user's contact list.
 * - `mine`: the logged-in user's own highlights.
 */
export function useHighlightsFeed(mode: HighlightsFeedMode) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const contacts = useContacts(mode === 'following' ? user?.pubkey : undefined);

  const authors: string[] | undefined =
    mode === 'mine'
      ? user
        ? [user.pubkey]
        : undefined
      : mode === 'following'
        ? contacts.data && contacts.data.length > 0
          ? contacts.data
          : undefined
        : undefined;

  const query = useInfiniteQuery({
    queryKey: ['nostr', 'highlights-feed', mode, user?.pubkey ?? '', authors?.length ?? 0],
    queryFn: async ({ pageParam }) => {
      const filter: NostrFilter = { kinds: [HIGHLIGHT_KIND], limit: PAGE_SIZE };
      if (pageParam) filter.until = pageParam;
      if (authors) filter.authors = authors;

      return nostr.query([filter], { signal: AbortSignal.timeout(5000) });
    },
    getNextPageParam: (lastPage) =>
      lastPage.length === 0 ? undefined : lastPage[lastPage.length - 1].created_at - 1,
    initialPageParam: undefined as number | undefined,
    enabled: mode === 'global' || authors !== undefined,
  });

  return { ...query, contacts };
}

/** Infinite feed of a specific author's highlights (for profile pages). */
export function useAuthorHighlightsFeed(pubkey: string) {
  const { nostr } = useNostr();

  return useInfiniteQuery({
    queryKey: ['nostr', 'highlights-author', pubkey],
    queryFn: async ({ pageParam }) => {
      const filter: NostrFilter = { kinds: [HIGHLIGHT_KIND], authors: [pubkey], limit: PAGE_SIZE };
      if (pageParam) filter.until = pageParam;

      return nostr.query([filter], { signal: AbortSignal.timeout(5000) });
    },
    getNextPageParam: (lastPage) =>
      lastPage.length === 0 ? undefined : lastPage[lastPage.length - 1].created_at - 1,
    initialPageParam: undefined as number | undefined,
  });
}

/** Highlights that reference a given article coordinate, event id, or URL(s). */
export function useHighlightsOfSource(source: { a?: string; e?: string; r?: string | string[] }) {
  const { nostr } = useNostr();

  const query = useQuery({
    queryKey: ['nostr', 'highlights-of', source.a ?? '', source.e ?? '', ...(Array.isArray(source.r) ? source.r : [source.r ?? ''])],
    queryFn: async () => {
      const filter: NostrFilter = { kinds: [HIGHLIGHT_KIND], limit: 200 };
      if (source.a) filter['#a'] = [source.a];
      else if (source.e) filter['#e'] = [source.e];
      else if (source.r) filter['#r'] = Array.isArray(source.r) ? source.r : [source.r];
      else return [];

      const events = await nostr.query([filter], { signal: AbortSignal.timeout(6000) });
      return events.sort((a, b) => b.created_at - a.created_at);
    },
    enabled: !!(source.a || source.e || (Array.isArray(source.r) ? source.r.length > 0 : source.r)),
  });

  const highlights = useParsedPages(query.data ? [query.data] : undefined);

  return { ...query, highlights };
}
